import React, { useState, useMemo, useCallback } from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import VariationBadgeList from './VariationBadgeList';

/**
 * VariationBadgeListDropdown — collapsible wrapper around `VariationBadgeList`
 * for dense, layout-constrained surfaces (notably the Voice Opportunities
 * Marketplace spreadsheet view).
 *
 * Default behavior: show up to `maxRows × columns` variation badges using the
 * underlying `VariationBadgeList`. If the variations array is longer, mask-fade
 * the visible grid toward the bottom and overlay a click-trigger chip in the
 * bottom-right ("+ N more"). The chip opens a Semantic UI `Popup` (portaled to
 * `document.body`) whose content is the **full** `VariationBadgeList` — so the
 * spreadsheet row below this cell is *not* pushed down when the user expands.
 *
 * Backward compat:
 *  - When `variations.length <= maxRows × columns` the wrapper returns an
 *    unmodified `<VariationBadgeList … />` — no extra DOM, no fade, no chip.
 *    Drop-in replacement; zero visual delta for short lists.
 *  - All `VariationBadgeList` props (`variations`, `columns`, `style`,
 *    `showIcon`, `showCloneSwatch`) are forwarded as-is. Palette, icons,
 *    per-badge hover popups, accessibility behavior all come from the
 *    underlying component — single source of truth preserved.
 *
 * Design decisions (filed 2026-05-14 in SESSION_RESUME_FEAT_VARIATION-BADGE-DROPDOWN_…):
 *  - Threshold = strict overflow (`len > visibleCount`). Trigger appears as
 *    soon as a single badge would be cropped. Predictable; the "+1 more"
 *    chip is acceptable.
 *  - Fade = `mask-image` linear-gradient on the inner grid (peek-behind).
 *    Polished look; degrades gracefully to a hard-cut grid with the chip
 *    still clickable in any browser that doesn't support `mask-image`.
 *  - Popover columns default to the same `columns` the call site passed —
 *    layout is consistent inline vs expanded; just more rows visible.
 *  - Popover portals via Semantic UI's default <Portal> to document.body,
 *    clearing the spreadsheet's stacking context. `zIndex: 1900` is set
 *    explicitly so future stacking changes elsewhere don't surprise us.
 *
 * @param {Object}   props
 * @param {Array}    props.variations          Forwarded to VariationBadgeList
 * @param {number}   [props.columns=2]         CSS grid columns (forwarded; also
 *                                              the popover's column count by default)
 * @param {number}   [props.maxRows=2]         Rows shown inline before truncation
 * @param {number}   [props.expandedColumns]   Optional override for popover columns.
 *                                              Defaults to `columns`.
 * @param {Object}   [props.style]             Forwarded to inline VariationBadgeList
 * @param {boolean}  [props.showIcon=true]     Forwarded
 * @param {boolean}  [props.showCloneSwatch]   Forwarded
 * @param {Function} [props.triggerLabel]      (remaining: number) => string
 * @param {string}   [props.popoverTitle]      Heading above the expanded list.
 *                                              Pass empty string '' to hide.
 * @param {number}   [props.popoverMaxWidth=480] Max width (px) for the popover
 */

// Height of the fade region at the bottom of the masked grid (px). The mask
// stays fully opaque until (100% - FADE_PX), then ramps to transparent.
const FADE_PX = 18;

// Locked explicitly even though Semantic UI's Popup defaults to 1900 — keeps
// the relative stacking order stable against future global changes.
const POPOVER_Z_INDEX = 1900;

// Width of one CSS grid cell, computed from `columns` and the 4px gap that
// `VariationBadgeList` hard-codes between its grid items. Letting the chip
// take exactly one cell's width means it sits *on top of* (and fully covers)
// the bottom-right badge in the visible 2×2 layout — the operator's spec.
//
// NOTE: this must stay in sync with `VariationBadgeList`'s `gap: '4px'`.
// If the badge list's gap ever changes, update GRID_GAP_PX here.
const GRID_GAP_PX = 4;
const cellWidthCalc = (columns) =>
  `calc((100% - ${(columns - 1) * GRID_GAP_PX}px) / ${columns})`;

// Only the *dynamic* bits — position + width — stay inline. Everything
// else (geometry, font, white fill, no border, lift shadow, hover/focus
// states) lives in `.aai-variation-badge.aai-variation-badge--more` in
// `variationBadges.css`, sharing the base class with the badges themselves
// so the chip pixel-aligns with the bottom-right badge it covers.
const triggerPositionStyleFor = (columns) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  // Width = exactly one grid-cell, so the chip covers the bottom-right badge.
  width: cellWidthCalc(columns),
});

const POPOVER_HEADER_STYLE = {
  fontWeight: 600,
  fontSize: '0.95rem',
  marginBottom: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const POPOVER_COUNT_STYLE = {
  fontWeight: 400,
  fontSize: '0.85rem',
  color: '#666',
};

const DEFAULT_TRIGGER_LABEL = (remaining) => `+ ${remaining} more`;

const VariationBadgeListDropdown = ({
  variations = [],
  columns = 2,
  maxRows = 2,
  expandedColumns,
  style = {},
  showIcon = true,
  showCloneSwatch = false,
  triggerLabel = DEFAULT_TRIGGER_LABEL,
  popoverTitle = 'All variations',
  popoverMaxWidth = 480,
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  // Mirror the validity filter inside `VariationBadgeList` so our truncation
  // math matches the badges actually rendered. Memoised so short-list re-renders
  // don't re-allocate the filtered array on every parent render.
  const validVariations = useMemo(
    () =>
      (Array.isArray(variations) ? variations : [])
        .filter((v) => v && v.uuid && v.emotion)
        .sort((a, b) =>
          String(a.emotion || '').toLowerCase().localeCompare(String(b.emotion || '').toLowerCase())
        ),
    [variations]
  );

  if (validVariations.length === 0) return null;

  const visibleCount = Math.max(1, maxRows * columns);
  const isTruncated = validVariations.length > visibleCount;

  // ----- Short-list branch -------------------------------------------------
  // Drop-in identical render — defer entirely to the existing component.
  if (!isTruncated) {
    return (
      <VariationBadgeList
        variations={validVariations}
        columns={columns}
        style={style}
        showIcon={showIcon}
        showCloneSwatch={showCloneSwatch}
      />
    );
  }

  // ----- Truncated branch --------------------------------------------------
  const visible = validVariations.slice(0, visibleCount);
  const remaining = validVariations.length - visibleCount;
  const expandedCols = typeof expandedColumns === 'number' ? expandedColumns : columns;

  // Vendor + standard mask gradient. Chromium/Safari read -webkit-, Firefox
  // reads the standard property. Browsers without either degrade to no fade —
  // the chip remains visible and clickable, just no peek-behind effect.
  const maskGradient = `linear-gradient(to bottom, #000 0%, #000 calc(100% - ${FADE_PX}px), transparent 100%)`;
  const maskedInnerStyle = {
    ...style,
    maskImage: maskGradient,
    WebkitMaskImage: maskGradient
  };

  const triggerButton = (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={`Show all ${validVariations.length} variations (${remaining} hidden)`}
      className="aai-variation-badge aai-variation-badge--more"
      style={triggerPositionStyleFor(columns)}
      onClick={handleOpen}
      // Stop propagation so clicking the chip inside a table-row doesn't
      // also trigger row-level click handlers if the parent ever binds one.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span>{triggerLabel(remaining)}</span>
      <Icon name="ellipsis horizontal" style={{ margin: 0, fontSize: '0.85rem' }} />
    </button>
  );

  const popoverContent = (
    <div role="region" aria-label="All variations">
      {popoverTitle ? (
        <div style={POPOVER_HEADER_STYLE}>
          <span>{popoverTitle}</span>
          <span style={POPOVER_COUNT_STYLE}>{validVariations.length} total</span>
        </div>
      ) : null}
      <VariationBadgeList
        variations={validVariations}
        columns={expandedCols}
        showIcon={showIcon}
        showCloneSwatch={showCloneSwatch}
      />
    </div>
  );

  return (
    // Positioning context for the absolute trigger chip. The Popup itself
    // portals to document.body and is unaffected by this wrapper's stacking.
    <div style={{ position: 'relative', display: 'block' }}>
      <VariationBadgeList
        variations={visible}
        columns={columns}
        style={maskedInnerStyle}
        showIcon={showIcon}
        showCloneSwatch={showCloneSwatch}
      />

      <Popup
        on="click"
        position="bottom right"
        flowing
        pinned
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        trigger={triggerButton}
        style={{
          zIndex: POPOVER_Z_INDEX,
          maxWidth: popoverMaxWidth,
          padding: '10px 12px',
        }}
        content={popoverContent}
      />
    </div>
  );
};

export default VariationBadgeListDropdown;
